import { useState } from "react";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { Link } from "react-router-dom";
import { allFilms, allSpecials } from "@/lib/catalog";
import { usePageMeta } from "@/hooks/usePageMeta";

const FORM_ENDPOINT = "https://readdy.ai/api/form/db9lnks6oi3g3tv8v0lg";

type SubmitStatus = "idle" | "submitting" | "success" | "error";

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

export default function AboutPage() {
  usePageMeta(
    "关于我们_日韩伦理片影视资料站定位与内容范围",
    "日韩伦理片网站的定位、内容范围、编辑原则与版权声明。了解我们如何整理日韩伦理影视作品资料，以及如何通过反馈表单联系编辑部。",
  );

  const [status, setStatus] = useState<SubmitStatus>("idle");
  const [feedback, setFeedback] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    const honey = String(formData.get("website_alt") || "").trim();

    if (honey) {
      setStatus("success");
      setFeedback("反馈已收到，感谢你的补充。");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string" && value.trim()) {
        payload.append(key, value.trim());
      }
    });

    setStatus("submitting");
    setFeedback("");

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }
      const code = parsed?.code;
      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || "";

      if (!response.ok || code !== "OK" || /spam/i.test(serverMessage)) {
        throw new Error(
          /spam/i.test(serverMessage)
            ? "提交未通过校验，请检查填写内容后重试。"
            : serverMessage || "提交失败，请稍后重试，或稍后再试一次。",
        );
      }

      setStatus("success");
      setFeedback("反馈已提交，感谢你帮助我们把资料做得更准确。");
      form.reset();
    } catch (error) {
      setStatus("error");
      setFeedback(
        error instanceof Error && error.message
          ? error.message
          : "提交失败，请检查网络后重试。",
      );
    }
  };

  return (
    <div>
      <section className="border-b border-background-200 bg-background-100/70">
        <div className="mx-auto max-w-shell px-4 pb-10 pt-6 md:px-8 md:pb-14 md:pt-8">
          <Breadcrumbs items={[{ label: "关于我们" }]} />
          <p className="font-label text-[11px] font-semibold uppercase tracking-[0.24em] text-accent-700">
            About The Archive
          </p>
          <h1 className="mt-2 max-w-3xl font-heading text-[32px] font-extrabold leading-[1.15] tracking-tight text-foreground-950 md:text-[46px]">
            一个关于日韩伦理影视的
            <br className="hidden sm:block" />
            资料库，而不是猎奇橱窗
          </h1>
          <p className="mt-4 max-w-2xl text-[13px] leading-[1.9] text-foreground-600 md:text-sm">
            我们相信，伦理题材值得被认真讨论。这里没有夸张的标题和截取的画面，只有作品信息、剧情脉络与编辑的解读，帮助读者建立自己的判断。
          </p>
          <div className="mt-6 flex flex-wrap gap-x-8 gap-y-3 font-label text-[11px] text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-book-open-line text-primary-600" />
              {allFilms.length} 条作品资料
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-folder-open-line text-primary-600" />
              {allSpecials.length} 个策划专题
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-shield-check-line text-primary-600" />
              仅面向成年读者
            </span>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-shell px-4 pt-12 md:px-8 md:pt-16">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[0.85fr_1.3fr] lg:gap-16">
          <div>
            <p className="flex items-center gap-2 font-label text-[11px] font-semibold uppercase tracking-[0.22em] text-accent-700">
              <span className="h-[2px] w-5 bg-accent-500" />
              Positioning
            </p>
            <h2 className="mt-2.5 font-heading text-[24px] font-extrabold leading-tight tracking-tight text-foreground-950 md:text-[30px]">
              网站定位与
              <br />
              目标读者
            </h2>
          </div>
          <div>
            <p className="text-[14px] leading-[1.95] text-foreground-700">
              本站面向对日韩伦理影视有深入了解需求的读者：影视爱好者、影评人、研究者，以及想系统补看某一类作品的观众。我们不做资源站，不提供在线播放，也不收录任何影像素材。
            </p>
            <p className="mt-4 text-[14px] leading-[1.95] text-foreground-600">
              所有条目围绕三个问题组织：这部作品讲了什么、它如何处理伦理困境、它在同类作品中的位置。分类、专题与搜索三条路径都指向同一套资料，确保列表和详情页的信息一致。
            </p>
            <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
              {[
                { icon: "ri-file-text-line", title: "作品资料", text: "导演、主演、年份、地区、时长与类型统一标注。" },
                { icon: "ri-article-line", title: "剧情解析", text: "每部作品附独立撰写的简介与三段式深度解析。" },
                { icon: "ri-links-line", title: "专题片单", text: "按主题重排作品，说明入选理由与观看顺序。" },
              ].map((item) => (
                <div key={item.title} className="rounded-lg border border-background-200 bg-background-100/60 p-4">
                  <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-50 text-primary-700">
                    <i className={`${item.icon} text-base`} />
                  </span>
                  <h3 className="mt-3 font-heading text-[14px] font-bold text-foreground-950">{item.title}</h3>
                  <p className="mt-1.5 text-[12px] leading-relaxed text-foreground-600">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-shell px-4 pt-12 md:px-8 md:pt-16">
        <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-8">
          <h2 className="font-heading text-[20px] font-extrabold text-foreground-950 md:text-[24px]">编辑原则</h2>
          <div className="mt-5 grid grid-cols-1 gap-x-10 gap-y-5 md:grid-cols-2">
            {[
              { title: "只讨论作品，不消费画面", text: "不描述露骨内容，不转载剧照特写，解析聚焦叙事、表演与议题。" },
              { title: "信息标注完整", text: "每条记录都保留导演、主演、年份、地区与时长，方便交叉核对。" },
              { title: "不制造虚假评价", text: "评分与热度为站内整理值，仅用于排序参考，不代表任何评分平台。" },
              { title: "保留不同解读空间", text: "解析是编辑观点，不是结论。我们尽量避免用单一立场覆盖作品。" },
            ].map((item, index) => (
              <div key={item.title} className="flex gap-3.5 border-t border-background-200 pt-4">
                <span className="font-label text-[11px] font-bold text-accent-700">{String(index + 1).padStart(2, "0")}</span>
                <div>
                  <h3 className="font-heading text-[14px] font-bold text-foreground-900">{item.title}</h3>
                  <p className="mt-1 text-[12px] leading-relaxed text-foreground-600">{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="feedback" className="mx-auto max-w-shell scroll-mt-28 px-4 pt-12 md:px-8 md:pt-16">
        <div className="grid grid-cols-1 gap-8 rounded-lg border border-background-200 bg-background-100/60 p-5 md:p-8 lg:grid-cols-[0.85fr_1.15fr] lg:gap-14">
          <div>
            <p className="flex items-center gap-2 font-label text-[11px] font-semibold uppercase tracking-[0.22em] text-accent-700">
              <span className="h-[2px] w-5 bg-accent-500" />
              Feedback
            </p>
            <h2 className="mt-2.5 font-heading text-[24px] font-extrabold leading-tight tracking-tight text-foreground-950 md:text-[28px]">
              内容反馈
            </h2>
            <p className="mt-3 text-[13px] leading-[1.9] text-foreground-600">
              发现条目信息有误、作品归类不合适，或者想推荐一部应该被收录的作品，都可以通过这个表单告诉我们。编辑部会定期整理并更新资料。
            </p>
            <div className="mt-5 space-y-2.5 text-[12px] text-foreground-600">
              <p className="flex items-start gap-2">
                <i className="ri-time-line mt-0.5 text-primary-600" />
                反馈会在 3 个工作日内进入整理队列（演示说明）。
              </p>
              <p className="flex items-start gap-2">
                <i className="ri-shield-line mt-0.5 text-primary-600" />
                仅收集你主动填写的联系邮箱，用于回复反馈。
              </p>
              <p className="flex items-start gap-2">
                <i className="ri-error-warning-line mt-0.5 text-primary-600" />
                请勿提交与作品资料无关的商业推广内容。
              </p>
            </div>
          </div>

          <form data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-3.5">
            <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
              <label className="flex flex-col gap-1.5">
                <span className="font-label text-[11px] text-foreground-500">称呼</span>
                <input
                  type="text"
                  name="name"
                  required
                  maxLength={40}
                  placeholder="怎么称呼你"
                  className="h-11 rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                />
              </label>
              <label className="flex flex-col gap-1.5">
                <span className="font-label text-[11px] text-foreground-500">联系邮箱</span>
                <input
                  type="email"
                  name="email"
                  required
                  maxLength={80}
                  placeholder="用于接收回复"
                  className="h-11 rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                />
              </label>
            </div>

            <label className="flex flex-col gap-1.5">
              <span className="font-label text-[11px] text-foreground-500">反馈类型</span>
              <select
                name="topic"
                defaultValue="条目信息纠错"
                className="h-11 cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none focus:border-primary-400"
              >
                <option value="条目信息纠错">条目信息纠错</option>
                <option value="作品推荐">作品推荐</option>
                <option value="解析意见">解析意见</option>
                <option value="版权与内容申诉">版权与内容申诉</option>
                <option value="其他">其他</option>
              </select>
            </label>

            <label className="flex flex-col gap-1.5">
              <span className="flex items-center justify-between font-label text-[11px] text-foreground-500">
                反馈内容
                <span className="text-foreground-400">最多 500 字</span>
              </span>
              <textarea
                name="message"
                required
                rows={5}
                maxLength={500}
                placeholder="请说明具体条目、问题所在或推荐理由。"
                className="resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
              />
            </label>

            <input
              type="text"
              name="website_alt"
              className="feedback-field-extra"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />

            {status !== "idle" && status !== "submitting" && (
              <p
                role="alert"
                className={`rounded-md px-3 py-2.5 text-[12px] leading-relaxed ${
                  status === "success"
                    ? "bg-accent-50 text-accent-900"
                    : "bg-background-200 text-foreground-800"
                }`}
              >
                {feedback}
              </p>
            )}

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-[11px] leading-relaxed text-foreground-400">
                提交即表示你同意我们使用该内容改进站内资料。
              </p>
              <button
                type="submit"
                disabled={status === "submitting"}
                className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-800 px-5 py-2.5 text-[13px] font-medium text-background-50 transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-secondary-400"
              >
                {status === "submitting" ? "提交中…" : "提交反馈"}
                <i className="ri-send-plane-line text-sm" />
              </button>
            </div>
          </form>
        </div>
      </section>

      <section id="terms" className="mx-auto max-w-shell scroll-mt-28 px-4 pt-12 md:px-8 md:pt-16">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <h2 className="font-heading text-[18px] font-bold text-foreground-950">版权声明</h2>
            <p className="mt-3 text-[12px] leading-[1.9] text-foreground-600">
              站内文字内容（简介、解析、专题导语）为本站编辑撰写，欢迎在注明出处的前提下引用与讨论，禁止整段转载或用于商业用途。作品名称、人物信息等事实性内容归原作者与出品方所有。
            </p>
            <p className="mt-2.5 text-[12px] leading-[1.9] text-foreground-600">
              本站不存储、不提供任何影视文件与播放链接。若权利人认为某条目存在信息错误或不当使用，可通过上方反馈表单提交申诉。
            </p>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <h2 className="font-heading text-[18px] font-bold text-foreground-950">服务条款（简述）</h2>
            <ul className="mt-3 space-y-2 text-[12px] leading-[1.9] text-foreground-600">
              <li>1. 本站为影视资料与评论站点，仅面向成年读者，不提供观看服务。</li>
              <li>2. 收藏、分享功能均在浏览器本地完成，不涉及账号注册与付费。</li>
              <li>3. 站内评分与热度为编辑整理值，仅用于内容排序参考。</li>
              <li>4. 请勿利用反馈表单提交违法、侵权或商业推广内容。</li>
            </ul>
            <p className="mt-3 text-[11px] text-foreground-400">
              当前版本为内容演示站点，功能与数据均用于展示产品形态。
            </p>
          </div>
        </div>

        <div className="mt-8 flex flex-col items-start justify-between gap-3 rounded-lg border border-background-200 bg-background-100/70 px-5 py-4 sm:flex-row sm:items-center">
          <p className="text-[12px] text-foreground-600">想先看看我们怎么整理作品？从编辑推荐开始最省时间。</p>
          <Link
            to="/specials"
            className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-800 px-4 py-2 text-[12px] font-medium text-background-50 transition-colors hover:bg-primary-700"
          >
            浏览专题合集
            <i className="ri-arrow-right-line text-xs" />
          </Link>
        </div>
      </section>
    </div>
  );
}